import { capitalize } from 'lodash';
import { useMemo } from 'react';

import { ClinicalRiskInputs, ToplineResult } from '@customTypes/order';
import { snakeToSentenceCase } from '@helpers/string';

interface IProps {
  patientInfo?: ClinicalRiskInputs;
  summary: ToplineResult[];
}

type ClinicalRiskField = Exclude<keyof ClinicalRiskInputs, 'sample'>;
type ClinicalRiskValue = ClinicalRiskInputs[ClinicalRiskField];

interface FieldDefinition {
  key: ClinicalRiskField;
  label: string;
  format: (value: ClinicalRiskValue) => string;
}

interface DisplayedInformation {
  label: string;
  value: string;
}

const formatBoolean = (value: ClinicalRiskValue): string => (value ? 'Yes' : 'No');
const formatNumber = (value: ClinicalRiskValue, unit: string): string => `${value} ${unit}`;
const formatSentenceCase = (value: ClinicalRiskValue): string =>
  snakeToSentenceCase(typeof value === 'string' ? value : null);

const FIELD_DEFINITIONS: FieldDefinition[] = [
  { key: 'age', label: 'Age', format: value => formatNumber(value, 'year-old') },
  { key: 'sex', label: 'Sex assigned at birth', format: value => capitalize(String(value)) },
  { key: 'race', label: 'Self-reported race/ethnicity', format: formatSentenceCase },
  { key: 'smoking_status', label: 'Smoking Status', format: formatSentenceCase },
  { key: 'diagnosed_with_type_2_diabetes', label: 'History of Diabetes', format: formatBoolean },
  { key: 'antihypertension_medication_use', label: 'Hypertension Treatment', format: formatBoolean },
  { key: 'total_cholesterol', label: 'Total Cholesterol', format: value => formatNumber(value, 'mg/dL') },
  { key: 'hdl_cholesterol', label: 'HDL Cholesterol', format: value => formatNumber(value, 'mg/dL') },
  {
    key: 'diastolic_blood_pressure',
    label: 'Diastolic Blood Pressure',
    format: value => formatNumber(value, 'mm Hg'),
  },
  {
    key: 'systolic_blood_pressure',
    label: 'Systolic Blood Pressure',
    format: value => formatNumber(value, 'mm Hg'),
  },
  { key: 'body_mass_index', label: 'Body Mass Index (kg/m^2)', format: value => String(value) },
  { key: 'waist_circumference', label: 'Waist Circumference (cm)', format: value => String(value) },
  {
    key: 'first_degree_family_with_type_2_diabetes',
    label: 'Has a First Degree Family Member Been Diagnosed with Type 2 Diabetes?',
    format: formatBoolean,
  },
  { key: 'fasting_glucose_level', label: 'Fasting Glucose Level (mg/dL)', format: value => String(value) },
  { key: 'triglyceride_level', label: 'Triglyceride Level (mg/dL)', format: value => String(value) },
  {
    key: 'first_or_second_degree_family_with_prostate_cancer',
    label: 'Has a First or Second Degree Family Member Been Diagnosed with Prostate Cancer?',
    format: formatSentenceCase,
  },
];

export function getRiskScoreInformation(patientInfo?: ClinicalRiskInputs): DisplayedInformation[] {
  if (!patientInfo) {
    return [];
  }

  return FIELD_DEFINITIONS.flatMap(({ key, label, format }) => {
    const value = patientInfo[key];
    return value === undefined ? [] : [{ label, value: format(value) }];
  });
}

export function hasRiskScoreSummary(patientInfo: ClinicalRiskInputs | undefined, summary: ToplineResult[]): boolean {
  return getRiskScoreInformation(patientInfo).length > 0 || summary.some(item => Boolean(item.details));
}

export function RiskScoreSummaryPane({ patientInfo, summary }: IProps) {
  const summarize = useMemo(() => summary.map(item => item.details).filter(Boolean), [summary]);
  const information = useMemo(() => getRiskScoreInformation(patientInfo), [patientInfo]);

  return (
    <div className='summary-pane result-details-tab-content'>
      {information.length > 0 && (
        <div className='risk-score-summary-info'>
          <p className='section-title'>Patient Demographics</p>
          <div className='mt-2 gap-1'>
            {information.map(({ label, value }) => (
              <p key={label}>
                <span className='fw-bold'>{`${label}: `}</span>
                {value}
              </p>
            ))}
          </div>
        </div>
      )}

      <div className={`summarize ${summary?.[0]?.finding?.toLowerCase() || ''}`}>
        {summarize.map((item, index) => (
          <p key={index}>{item}</p>
        ))}
      </div>
    </div>
  );
}
